import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Alert,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { Settings, Save } from '@mui/icons-material';
import api from '../../api/axios';

const SiteSettings = () => {
    const queryClient = useQueryClient();

    const [smsCreditRate, setSmsCreditRate] = useState('');
    const [whatsappCreditRate, setWhatsappCreditRate] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['site-settings'],
        queryFn: async () => {
            const response = await api.get('/superadmin/site-settings');
            return response.data;
        },
    });

    useEffect(() => {
        if (data?.data) {
            setSmsCreditRate(data.data.sms_credit_rate ?? '');
            setWhatsappCreditRate(data.data.whatsapp_credit_rate ?? '');
        }
    }, [data]);

    const updateMutation = useMutation({
        mutationFn: async (payload: { sms_credit_rate: string; whatsapp_credit_rate: string }) => {
            return api.put('/superadmin/site-settings', payload);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['site-settings'] });
            setSuccess('Settings saved successfully');
            setError('');
            setTimeout(() => setSuccess(''), 3000);
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
            setSuccess('');
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        updateMutation.mutate({
            sms_credit_rate: smsCreditRate,
            whatsapp_credit_rate: whatsappCreditRate,
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Settings sx={{ fontSize: 32, color: '#7B4DFF' }} />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Site Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage global credit rates for SMS and WhatsApp
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Card sx={{ maxWidth: 600, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                label="SMS Credit Rate"
                                type="number"
                                value={smsCreditRate}
                                onChange={(e) => setSmsCreditRate(e.target.value)}
                                required
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    },
                                    htmlInput: { min: 0, step: '0.01' },
                                }}
                                helperText="Cost per SMS credit"
                            />
                            <TextField
                                label="WhatsApp Credit Rate"
                                type="number"
                                value={whatsappCreditRate}
                                onChange={(e) => setWhatsappCreditRate(e.target.value)}
                                required
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                    },
                                    htmlInput: { min: 0, step: '0.01' },
                                }}
                                helperText="Cost per WhatsApp credit"
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<Save />}
                                disabled={updateMutation.isPending}
                                sx={{
                                    alignSelf: 'flex-start',
                                    bgcolor: '#7B4DFF',
                                    '&:hover': { bgcolor: '#6A3DE8' },
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SiteSettings;
